import React, { useCallback, useState } from 'react';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';

export interface BlockDragState {
  widgetUUID: string;
  /** The index where the row lands. */
  overIndex: number;
  /** True for a keyboard move (Space, arrows, Space). */
  isKeyboard: boolean;
}

interface UseBlockDragProps {
  ordered: readonly SerializedWidget[];
  moveTo: (widgetUUID: string, toIndex: number) => void;
}

/**
 * Drag to reorder (design a7): the handle drags with the mouse (HTML drag and drop) or with the
 * keyboard: focus the handle, Space to pick up, the arrow keys to move, Space to drop, Escape to
 * cancel. On drop, one PATCH of `position` (`useBlockOrder.moveTo`).
 */
export function useBlockDrag({ ordered, moveTo }: UseBlockDragProps) {
  const [drag, setDrag] = useState<BlockDragState | null>(null);
  const indexOf = useCallback((uuid: string) => ordered.findIndex((widget) => widget.uuid === uuid), [ordered]);

  const drop = useCallback(() => {
    if (drag !== null) {
      moveTo(drag.widgetUUID, drag.overIndex);
    }
    setDrag(null);
  }, [drag, moveTo]);

  const cancel = useCallback(() => setDrag(null), []);

  const onDragStart = useCallback(
    (widgetUUID: string, event: React.DragEvent) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', widgetUUID);
      setDrag({ widgetUUID, overIndex: indexOf(widgetUUID), isKeyboard: false });
    },
    [indexOf]
  );

  const onDragOver = useCallback(
    (index: number, event: React.DragEvent) => {
      if (drag === null || drag.isKeyboard) {
        return;
      }
      event.preventDefault();
      if (drag.overIndex !== index) {
        setDrag({ ...drag, overIndex: index });
      }
    },
    [drag]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      drop();
    },
    [drop]
  );

  const onHandleKeyDown = useCallback(
    (widgetUUID: string, event: React.KeyboardEvent) => {
      const isPickKey = event.key === ' ' || event.key === 'Enter';
      if (drag === null || drag.widgetUUID !== widgetUUID) {
        if (isPickKey) {
          event.preventDefault();
          setDrag({ widgetUUID, overIndex: indexOf(widgetUUID), isKeyboard: true });
        }
        return;
      }
      if (isPickKey) {
        event.preventDefault();
        drop();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const delta = event.key === 'ArrowUp' ? -1 : 1;
        const overIndex = Math.max(0, Math.min(ordered.length - 1, drag.overIndex + delta));
        setDrag({ ...drag, overIndex });
      }
    },
    [cancel, drag, drop, indexOf, ordered.length]
  );

  return { drag, onDragStart, onDragOver, onDrop, onDragEnd: cancel, onHandleKeyDown, cancel };
}

export type BlockDrag = ReturnType<typeof useBlockDrag>;
